import { RootState } from 'store';
import { useMemo } from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  checkIsLoading,
  checkIsLoadingDone
} from 'common/utils/checkLoading';
import { turfBounds } from 'shared/utils';

import { CARS_STATE } from '../../constants';

import { EMPTY_ARRAY } from 'shared/constants';

import { CarEBoxLocation, CarEBoxLocations, CarsState } from '../../types';

type CarsFilter = CarsState['eBoxLocations']['filter'];

const useCarEBoxLocationsLoadingStatus = () => (
  useSelector((state: RootState) => state[CARS_STATE].eBoxLocations.loadingStatus)
);

export const useShowCarEBoxLocations = () => (
  useSelector((state: RootState) => state[CARS_STATE].eBoxLocations.show)
);

export const useIsCarEBoxLocationsLoading = () => {
  const carEBoxLocationsLoadingStatus = useCarEBoxLocationsLoadingStatus();
  return checkIsLoading(carEBoxLocationsLoadingStatus);
};

export const useIsCarEBoxLocationsDone = () => {
  const carEBoxLocationsLoadingStatus = useCarEBoxLocationsLoadingStatus();
  return checkIsLoadingDone(carEBoxLocationsLoadingStatus);
};

export const useCarEBoxLocations = () => (
  useSelector((state: RootState) => state[CARS_STATE].eBoxLocations.data)
);

export const useCars = () => {
  const cars = useSelector((state: RootState) => state[CARS_STATE].eBoxLocations.data);

  return useMemo(() => {
    if (cars) {
      const sortedCars = [...cars];
      sortedCars.sort((firstItem, secondItem) => {
        const firstNumber = firstItem.autoNumber.toLowerCase();
        const secondNumber = secondItem.autoNumber.toLowerCase();

        if (firstNumber === secondNumber) {
          return 0;
        }

        return firstNumber > secondNumber ? 1 : -1;
      });
      return sortedCars;
    }

    return cars;
  }, [cars]);
};

export const useCarsFilter = () => (
  useSelector((state: RootState) => state[CARS_STATE].eBoxLocations.filter)
);

export const useHasSelectedCarsFilderStatus = () => {
  const { status = [] } = useCarsFilter();
  return !!status.length;
};

export const useFilteredCarsByStatus = () => {
  const cars = useCars() || EMPTY_ARRAY;
  const { status = EMPTY_ARRAY } = useCarsFilter();

  return useMemo(() => status.length ? cars.reduce((acc, car) => {
    if (status.includes(car.status.toString())) {
      acc.push(car);
    }
    return acc;
  }, [] as CarEBoxLocation[]) : cars, [
    cars,
    status
  ]);
};

export const useGetCarBounds = () => (
  useCallback((cars: CarEBoxLocations) => {
    const coords = cars.reduce((acc, { latitude, longitude }) => {
      acc.push([ longitude, latitude ]);
      return acc;
    }, [] as [number, number][]);

    return coords.length ? turfBounds(coords) : null
  }, [])
);

export const useGetFilteredCars = () => {
  const cars = useCars() || EMPTY_ARRAY;

  return useCallback(({ search = '', status = EMPTY_ARRAY }: CarsFilter) => {
    const trimedSearch = search.trim();

    return (trimedSearch || status.length) ? cars.reduce((acc, car) => {
      if (
        car.autoNumber.toLowerCase().includes(trimedSearch.toLowerCase()) &&
        (!status.length || status.includes(car.status.toString()))
      ) {
        acc.push(car);
      }

      return acc;
    }, [] as CarEBoxLocation[]) : cars;
  }, [cars]);
};

export const useFilteredCars = () => {
  const getFilteredCars = useGetFilteredCars();
  const { search, status } = useCarsFilter();

  return useMemo(() => getFilteredCars({ search, status }), [
    getFilteredCars,
    search,
    status
  ]);
};

export const useFilterCarEBoxLocationsByAutoNumberOrObuNumber = () => {
  const carEBoxLocations = useCarEBoxLocations() || EMPTY_ARRAY;

  const filterCarEBoxLocationsByAutoNumberOrObuNumber = useCallback((value: string) => (
    carEBoxLocations.filter(({ autoNumber, obuNumber }) => (
      autoNumber.toLowerCase().includes(value.toLowerCase()) ||
      obuNumber.toLowerCase().includes(value.toLowerCase())
    ))
  ), [carEBoxLocations]);

  return filterCarEBoxLocationsByAutoNumberOrObuNumber;
};

export const useHasCarEBoxLocations = () => {
  const carEBoxLocations = useCarEBoxLocations() || [];
  return !!carEBoxLocations.length;
};
